import React, { useContext, useEffect, useMemo } from "react";
import AuthGuard from "../components/guards/AuthGuard";
import Sidebar from "../components/ui/sidebar/Sidebar";
import MainBody from "../components/ui/mainbody/MainBody";
import { SidebarContext } from "../contexts/SidebarContext";
import { InlineLogisticsLoader } from "../components/ui/spinner";
import Table, { Column } from "../components/ui/table/Table";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import { fetchCustomerInvoices } from "../redux/slices/invoicesSlice";
// put these above your component (same file is fine)
const toDateSafe = (v: unknown): Date | null => {
  if (!v) return null;

  // Already a Date
  if (v instanceof Date && !isNaN(v.getTime())) return v;

  // Numeric epoch (seconds or ms)
  if (typeof v === "number") {
    const ms = v < 1e12 ? v * 1000 : v;
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d;
  }

  const s = String(v).trim();

  // If it's already like "16 Aug 2025", just skip parsing
  if (/[A-Za-z]{3}/.test(s)) return new Date(s); // may succeed; if not, we'll just return the original string later

  // Try ISO-ish first
  let d = new Date(s.replace(" ", "T"));
  if (!isNaN(d.getTime())) return d;

  // yyyy-mm-dd[ hh:mm[:ss]]
  let m = s.match(
    /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (m) {
    d = new Date(
      Number(m[1]),
      Number(m[2]) - 1,
      Number(m[3]),
      Number(m[4] || 0),
      Number(m[5] || 0),
      Number(m[6] || 0)
    );
    return isNaN(d.getTime()) ? null : d;
  }

  // dd-mm-yyyy or dd/mm/yyyy [hh:mm[:ss]]
  m = s.match(
    /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (m) {
    d = new Date(
      Number(m[3]),
      Number(m[2]) - 1,
      Number(m[1]),
      Number(m[4] || 0),
      Number(m[5] || 0),
      Number(m[6] || 0)
    );
    return isNaN(d.getTime()) ? null : d;
  }

  return null;
};

const formatDDMonYYYY = (d: Date) =>
  d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
type Row = {
  id: string;
  invoice_no: string;
  payment_mode: string;
  invoice_date: string; // pretty
  invoice_link: string;
};

const columns: Column<Row>[] = [
  {
    header: "Invoice No.",
    accessor: "invoice_no",
    cell: (value) => <strong className="text-dark">Inv# : {value}</strong>, // 👈 Custom display format
  },
  { header: "Payment Mode", accessor: "payment_mode" },
  {
  header: "Invoice Date",
  accessor: "invoice_date",
  cell: (v) => {
    if (!v) return "-";

    // If it already looks like "16 Aug 2025", just show it
    if (typeof v === "string" && /(\d{1,2})\s+[A-Za-z]{3}\s+\d{4}/.test(v)) {
      return v;
    }

    const d = toDateSafe(v);
    return d ? formatDDMonYYYY(d) : String(v); // fallback to raw if still unparseable
  },
},
  {
    header: "View Invoice",
    accessor: "invoice_link",
    cell: (link) =>
      link ? (
        <a
          href={String(link)}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-outline-danger btn-lg"
        >
          Open
        </a>
      ) : (
        "-"
      ),
  },
];

const MyInvoices = () => {
  const { isSidebarCollapsed } = useContext(SidebarContext);
  const dispatch = useDispatch<AppDispatch>();

  const { items, loading, error } = useSelector((s: RootState) => s.invoices);

  useEffect(() => {
    dispatch(fetchCustomerInvoices());
  }, [dispatch]);

  const rows: Row[] = useMemo(
    () =>
      items.map((inv) => ({
        id: inv.id,
        invoice_no: inv.invoice_no,
        payment_mode: inv.payment_mode,
        invoice_date: inv.invoice_date_str || inv.invoice_date, // prefer pretty
        invoice_link: inv.invoice_link,
      })),
    [items]
  );

  return (
    <AuthGuard
      requireAuth
      redirectTo="/"
      allowedRoles={["customer", "admin", "user"]}
      showLoadingSpinner
    >
      <section id="trackShipmentSection">
        <div className="container-fluid p-0">
          <div
            className={`bodyWrap ${
              isSidebarCollapsed ? "sidebar-collapsed" : ""
            }`}
          >
            <Sidebar />
            <MainBody>
              <div className="container-fluid">
                <div className="tableWraper">
                  {loading ? (
                    <div className="d-flex justify-content-center py-5">
                      <InlineLogisticsLoader />
                    </div>
                  ) : error ? (
                    <div className="alert alert-danger">{error}</div>
                  ) : rows.length === 0 ? (
                    <div className="text-muted py-4">
                      No invoices found for your account.
                    </div>
                  ) : (
                    <Table<Row>
                      columns={columns}
                      data={rows}
                      sectionTitle="My Invoices"
                      rowIdAccessor="id"
                    />
                  )}
                </div>
              </div>
            </MainBody>
          </div>
        </div>
      </section>
    </AuthGuard>
  );
};

export default MyInvoices;
